import Vue from 'vue';
import resetState from '@/helpers/resetState';
import store from '@/store';
import { paramsSerializer } from '@/helpers/serializers';
import { formatData } from '@/constants/commonContent';
import { messages } from '@/constants/messages';
import CONFIG from '@/config';
import { ABI } from './ABI';
import axios from 'axios';
import { Contract } from 'web3-eth-contract';

const FEATURE_NAME = 'CompanyWalletFeature';

let from;

async function contract(): Promise<Contract> {
  from = {from: store.getters.getWallet};
  const address = store.getters.getFeatureAddress(FEATURE_NAME);
  return new Promise(resolve => {
    let contract = new window.web3.eth.Contract(ABI, address);
    resolve(contract);
  });
}

const initialState = {
  companyEthereumWallet: {},
  companyBitcoinWallet: {},
  companyBankWallet: {},
  companyBitcoinBalance: 0,
  companyEthereumBalance: 0
};

const state = {
  companyEthereumWallet: initialState.companyEthereumWallet,
  companyBitcoinWallet: initialState.companyBitcoinWallet,
  companyBankWallet: initialState.companyBankWallet,
  companyBitcoinBalance: initialState.companyBitcoinBalance,
  companyEthereumBalance: initialState.companyEthereumBalance
};

const getters = {
  getCompanyEthereumWallet: state => state.companyEthereumWallet,
  getCompanyBitcoinWallet: state => state.companyBitcoinWallet,
  getCompanyBankWallet: state => state.companyBankWallet,
  getCompanyBitcoinBalance: state => `${state.companyBitcoinBalance}`,
  getCompanyEthereumBalance: state => `${state.companyEthereumBalance}`
};

const mutations = {
  resetCompanyWalletFeature: state => {
    resetState(state, initialState);
  },
  setCompanyEthereumWallet: (state, item) => {
    state.companyEthereumWallet = item;
  },
  setCompanyBitcoinWallet: (state, item) => {
    state.companyBitcoinWallet = item;
  },
  setCompanyBankWallet: (state, item) => {
    state.companyBankWallet = item;
  },
  setCompanyEthereumBalance: (state, item) => {
    state.companyEthereumBalance = item;
  },
  setCompanyBitcoinBalance: (state, item) => {
    state.companyBitcoinBalance = item;
  }
};

const actions = {
  initCompanyWalletFeature({ dispatch, commit }) {
    dispatch('getCompanyEthereumWalletDapp').then(data => {
      dispatch('getCompanyEthereumBalanceExt', data.address);
    });
    dispatch('getCompanyBitcoinWalletDapp').then(data => {
      dispatch('getCompanyBitcoinBalanceExt', data.address);
    });
    dispatch('getCompanyBankWalletDapp');
    commit('companyWalletEthPending', false);
    commit('companyWalletBtcPending', false);
    commit('companyWalletBankPending', false);
    //Events
    contract().then(contract => {
      let transactionHash = '0x';
      contract.events.CompanyWalletEvent().subscribe((err, res) => {
        if (!err && transactionHash !== res.transactionHash) {
          transactionHash = res.transactionHash;
          switch (res.returnValues.statusId) {
            case '1':
              dispatch('getCompanyEthereumWalletDapp').then(data => {
                dispatch('getCompanyEthereumBalanceExt', data.address);
              });
              commit('companyWalletEthPending', false);
              break;
            case '2':
              dispatch('getCompanyBitcoinWalletDapp').then(data => {
                dispatch('getCompanyBitcoinBalanceExt', data.address);
              });
              commit('companyWalletBtcPending', false);
              break;
            case '3':
              dispatch('getCompanyBankWalletDapp');
              commit('companyWalletBankPending', false);
              break;
            default:
              console.error('Unknown CompanyWalletEvent status');
          }
        }
      });
    });
  },
  /**
   * Get information about company ethereum wallet
   * @return {Promise<Object>}
   * res[0] "address creator" {string}
   * res[1] "address wallet" {string}
   * res[2] "time" {number}
   */
  getCompanyEthereumWalletDapp({ commit }) {
    return new Promise(resolve => {
      contract().then(contract => {
        contract.methods.getEthereumWallet()
          .call()
          .then(res => {
            const data = {
              creator: res[0],
              address: res[1],
              time: parseInt(res[2])
            };
            commit('setCompanyEthereumWallet', data);
            resolve(data);
          })
          .catch(console.log);
      });
    });
  },
  /**
   * Get information about company Bitcoin wallet
   * @return {Promise<Object>}
   * res[0] "address creator" {string}
   * res[1] "address wallet" {string}
   * res[2] "time" {number}
   */
  getCompanyBitcoinWalletDapp({ commit }) {
    return new Promise(resolve => {
      contract().then(contract => {
        contract.methods.getBitcoinWallet()
          .call()
          .then(res => {
            const data = {
              creator: res[0],
              address: res[1],
              time: parseInt(res[2])
            };
            commit('setCompanyBitcoinWallet', data);
            resolve(data);
          });
      });
    });
  },
  /**
   * Get information about Bank wallet
   * res[0] "address creator" {string}
   * res[1] "name" {string}
   * res[2] "currency" {string}
   * res[3] "value" {string}
   * res[2] "time" {number}
   */
  getCompanyBankWalletDapp({ commit }) {
    contract().then(contract => {
      contract.methods.getBankWallet()
        .call()
        .then(res => {
          commit('setCompanyBankWallet', {
            creator: res[0],
            name: res[1],
            currency: res[2],
            value: res[3],
            time: parseFloat(res[4])
          });
        })
        .catch(console.error);
    });
  },
  /**
   * Get ethereum balance for company wallet
   * @params {Object}
   * @params params.module {string}
   * @params params.action {string}
   * @params params.address {string}
   * @params params.tag {string}
   * @params params.apiKey {string}
   * @result data.result {number}
   */
  getCompanyEthereumBalanceExt({ commit, getters }, address) {
    CONFIG.etherscan!.balance.params.address = address;
    axios
      .get(CONFIG.etherscan!.url, {
        params: CONFIG.etherscan!.balance.params,
        paramsSerializer
      })
      .then(res => {
        if (res.data['message'] === 'OK') {
          const balance = formatData.number(formatData.fromWei(res.data['result']), 5);
          commit('setCompanyEthereumBalance', balance);
        } else {
          commit('setCompanyEthereumBalance', getters.getCompanyBitcoinBalance);
        }
      });
  },
  /**
   * Get bitcoin balance for company wallet
   * @params {Object}
   * @params params.active {string}
   * @params params.action {Boolean}
   * @result data.result {number}
   */
  getCompanyBitcoinBalanceExt({ commit, getters }, address) {
    CONFIG.blockchain!.balance.params.active = address;
    axios
      .get(CONFIG.blockchain!.url + CONFIG.blockchain!.balance.uri, {
        params: CONFIG.blockchain!.balance.params,
        paramsSerializer
      })
      .then(res => {
        if (res.data[address]) {
          const balance = formatData.number(
            `${formatData.fromSatoshiToBTC(res.data[address].final_balance)}`,
            5
          );
          commit('setCompanyBitcoinBalance', balance);
        }
      })
      .catch(() => commit('setCompanyBitcoinBalance', getters.getCompanyBitcoinBalance));
  },
  /**
   * Add ethereum wallet for company
   * @params {Object}
   * @params data.address {string}
   */
  setCompanyEthereumWalletDapp({ commit }, data) {
    contract()
      .then(contract => {
        contract.methods.setEthereumWallet(data.address)
          .send(from, (err) => {
            if (err) {
              commit('companyWalletEthPending', false);
              Vue.toasted.error(messages.canceled);
            }
          });
      })
      .catch(() => {
        commit('companyWalletEthPending', false);
        Vue.toasted.error(messages.canceled);
      });
  },
  /**
   * Add bitcoin wallet for company
   * @params {Object}
   * @params data.address {string}
   */
  setCompanyBitcoinWalletDapp({ commit }, data) {
    contract()
      .then(contract => {
        contract.methods.setBitcoinWallet(data.address)
          .send(from, (err) => {
            if (err) {
              commit('companyWalletBtcPending', false);
              Vue.toasted.error(messages.canceled);
            }
          });
      })
      .catch(() => {
        commit('companyWalletBtcPending', false);
        Vue.toasted.error(messages.canceled);
      });
  },
  /**
   * Set bank wallet for company
   * @params {Object}
   * @params data.name {string}
   * @params data.currency {string}
   * @params data.value {string}
   */
  setCompanyBankWalletDapp({ commit }, data) {
    contract()
      .then(contract => {
        contract.methods.setBankWallet(data.name, data.currency, data.value)
          .send(from, (err) => {
            if (err) {
              commit('companyWalletBankPending', false);
              Vue.toasted.error(messages.canceled);
            }
          });
      })
      .catch(() => {
        commit('companyWalletBankPending', false);
        Vue.toasted.error(messages.canceled);
      });
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
